import { getDeepError } from '@polaris/core/utils'

/**
 * Provides a wrapper that adds a default unknown error message and keeps original
 * data into the raw field.
 */
class UnknownError extends Error {
  constructor(error: Error | string | undefined) {
    super(
      'An unknown error occurred. Retry or contact support for more assistance.',
      {
        cause: error,
      },
    )
  }
}

/**
 * Provides a wrapper that adds a default internal server error message and keeps
 * original data into the raw field.
 */
class InternalServerError extends Error {
  constructor(error: Error | string | undefined) {
    super(
      'An internal server error occurred. Retry or contact support for more assistance.',
      {
        cause: error,
      },
    )
  }
}

/**
 * Wrapper for errors regarding message creation
 */
class MessageCreationError extends Error {
  constructor(error: Error | string | undefined) {
    super(
      'An error occurred while creating the trade message. Check destination address.',
      {
        cause: error,
      },
    )
  }
}

/**
 * Wraps any function to catch errors and categorize them using the given error
 * categorization function.
 */
export function wrapCatchCategorize<
  TArgs extends unknown[],
  TReturn,
  TError extends Error,
>(fn: (...args: TArgs) => TReturn, categorize: (e: TError) => never) {
  return (...args: TArgs): TReturn => {
    try {
      const r = fn(...args)
      if (r instanceof Promise) {
        return r.catch(categorize) as TReturn
      }
      return r
    } catch (e) {
      categorize(e as TError)
    }
  }
}

export function getErrorToDisplay(error: Error | undefined): Error | undefined {
  const lastCauseError = getDeepError(error)
  if (lastCauseError === undefined) return undefined
  if (typeof lastCauseError?.message === 'string') {
    try {
      const bodyError = JSON.parse(lastCauseError.message)
      if (bodyError.code === 'AutomaticallyHandledError') {
        return new InternalServerError(lastCauseError)
      }
      if (bodyError.code === 'MessageCreationError') {
        return new MessageCreationError(lastCauseError)
      }
      if ('code' in bodyError) {
        return new InternalServerError(lastCauseError)
      }
    } catch {
      return lastCauseError
    }
  }
  return new UnknownError(error)
}
